import classnames from 'classnames';
import React, { useContext, useEffect } from 'react';

import type { MediaUploadError } from './../../ricos-content';
import { RicosContext } from './../../ricos-context';
import type { FileData } from './../../ricos-schema/legacy/wix/rich_content/v1';
import { Node_Type } from './../../ricos-schema/legacy/wix/rich_content/v1';
import type { Helpers, LegacyEditorPluginConfig } from './../../ricos-types';
import { isDefined } from './../../wix-rich-content-common';
import { DoubleLoader } from './../../ui-components/index';

import {
  FILE_UPLOAD_EXTENSION,
  FILE_UPLOAD_NAME,
  FILE_UPLOAD_NAME_CONTAINER,
  ROOT,
} from '../data-hooks';
import { DownloadIcon } from '../icons/download-icon';
import { ErrorIcon } from '../icons/error-icon';
import { getIcon } from '../icons/get-icon';
import styles from '../statics/styles/file-upload-viewer-scss';
import { FILE_UPLOAD_TYPE } from '../types';

const KB = 1000; // we're using "metric" KB (not kibibytes)
const MB = KB * 1000;

const getNameWithoutType = (fileName: string) => {
  if (!fileName) {
    return fileName;
  }

  const lastDotIndex = fileName.lastIndexOf('.');
  return lastDotIndex !== -1 ? fileName.substring(0, lastDotIndex) : fileName;
};

const getFormattedFileSize = (
  componentData: FileData,
  tempDataPlaceHolder?: FileData
): string | undefined => {
  const sizeInKb = componentData.sizeInKb ?? tempDataPlaceHolder?.sizeInKb;
  const size = componentData.size ?? tempDataPlaceHolder?.size;

  if (sizeInKb && !isNaN(parseFloat(sizeInKb))) {
    return formatFileSize(parseFloat(sizeInKb) * KB);
  }

  if (size) {
    return formatFileSize(size);
  }

  return undefined;
};

const formatFileSize = (sizeInBytes: number) => {
  if (sizeInBytes < KB) {
    return `${Math.round(sizeInBytes)}B`;
  }

  if (sizeInBytes < MB) {
    return `${Math.round(sizeInBytes / KB)}KB`;
  }

  return `${(sizeInBytes / MB).toFixed(2)}MB`;
};

type FileViewerContentProps = {
  state: 'loading' | 'error' | 'ready';
  fileData: FileData;
};

const FileViewerContent: React.FC<FileViewerContentProps> = ({ state, fileData }) => {
  const { t } = useContext(RicosContext);
  const { name, type } = fileData;

  if (!isDefined(name) || !isDefined(type)) {
    return null;
  }

  const TypeIcon = getIcon(type);
  const fileBasename = getNameWithoutType(name);
  const fileExtension = type ? `.${type}` : undefined;

  let infoString: string;

  if (state === 'error') {
    infoString = t('UploadFile_Error_Generic_Item');
  } else {
    const translationKey =
      state === 'loading' ? 'UploadFile_Viewer_Loader' : 'UploadFile_Viewer_Download';
    const fileType = type?.toUpperCase() ?? '';
    const formattedFileSize = getFormattedFileSize(fileData);
    const fileSizeSuffix = formattedFileSize ? ` • ${formattedFileSize}` : '';
    infoString = `${t(translationKey, { fileType })}${fileSizeSuffix}`;
  }

  return (
    <>
      <TypeIcon className={styles.file_upload_type_icon} />
      <div className={styles.file_upload_description}>
        <div className={styles.file_upload_name_container} data-hook={FILE_UPLOAD_NAME_CONTAINER}>
          <span className={styles.file_upload_name} data-hook={FILE_UPLOAD_NAME}>
            {fileBasename}
          </span>
          {fileExtension && (
            <span className={styles.file_upload_extension} data-hook={FILE_UPLOAD_EXTENSION}>
              {fileExtension}
            </span>
          )}
        </div>
        <div className={styles.file_upload_file_info}>{infoString}</div>
      </div>
      <div className={styles.file_upload_status_icon_container}>
        {state === 'loading' && <DoubleLoader className={styles.file_loader_icon} />}
        {state === 'error' && <ErrorIcon className={styles.file_upload_status_icon} />}
        {state === 'ready' && <DownloadIcon className={styles.file_upload_status_icon} />}
      </div>
    </>
  );
};

export const FileViewer: React.FC<{
  settings: LegacyEditorPluginConfig;
  componentData: FileData & { error?: MediaUploadError };
  isLoading?: boolean;
  helpers?: Helpers;
  tempDataPlaceHolder?: FileData;
  nodeId: string;
}> = ({ settings, componentData, isLoading = false, tempDataPlaceHolder, helpers, nodeId }) => {
  const { tabIndex } = useContext(RicosContext);
  const linkRef = React.useRef<HTMLAnchorElement | null>(null);
  const [shouldDownloadOnRender, setShouldDownloadOnRender] = React.useState<boolean>(false);
  const [resolvedFileUrl, setResolvedFileUrl] = React.useState<string | undefined>(undefined);
  const [isResolvingUrl, setResolvingUrl] = React.useState<boolean>(false);
  const state = isLoading || isResolvingUrl ? 'loading' : componentData.error ? 'error' : 'ready';

  useEffect(() => {
    if (!shouldDownloadOnRender) {
      return;
    }

    linkRef.current?.click();
    setShouldDownloadOnRender(false);
  }, [shouldDownloadOnRender]);

  const onFileClick = () => {
    if (componentData.name) {
      helpers?.onViewerAction?.(FILE_UPLOAD_TYPE, 'Click', componentData.name, nodeId, {
        type: Node_Type.FILE,
        fileData: componentData,
      });
    }
  };

  const renderContainerWithoutLink = () => {
    return (
      <div className={styles.file_upload_link}>
        <FileViewerContent state={state} fileData={tempDataPlaceHolder || componentData} />
      </div>
    );
  };

  const renderDownloadLink = (fileUrl: string) => {
    return (
      <a
        data-cancel-link
        ref={linkRef}
        className={styles.file_upload_link}
        tabIndex={tabIndex}
        href={fileUrl}
        target={settings.downloadTarget}
        download
        onClick={onFileClick}
      >
        <FileViewerContent state={state} fileData={componentData} />
      </a>
    );
  };

  const renderResolverButton = () => {
    const handleClick = () => {
      if (isResolvingUrl) {
        return;
      }

      setResolvingUrl(true);

      settings
        .resolveFileUrl({
          id: componentData?.src?.id,
          type: componentData.type,
          path: componentData.path,
          mimeType: componentData.mimeType,
          name: componentData.name,
          size: componentData.size,
          pdfSettings: componentData.pdfSettings,
          privacy: componentData?.src?.private ? 'private' : 'public',
        })
        .then((resolvedFileUrl: string) => {
          setResolvedFileUrl(resolvedFileUrl);
          setResolvingUrl(false);
          setShouldDownloadOnRender(true);
        });
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === 'Enter') {
        handleClick();
      }
    };

    return (
      <button
        type="button"
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className={styles.file_upload_link}
      >
        <FileViewerContent state={state} fileData={componentData} />
      </button>
    );
  };

  const renderContent = () => {
    if (componentData.error || tempDataPlaceHolder) {
      return renderContainerWithoutLink();
    }

    const fileUrl = componentData.src?.url || resolvedFileUrl;
    return fileUrl ? renderDownloadLink(fileUrl) : renderResolverButton();
  };

  return (
    <div
      data-hook={ROOT}
      className={classnames(
        styles.file_upload_container,
        componentData.error && styles.file_upload_error_container
      )}
    >
      {renderContent()}
    </div>
  );
};
